import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import { getCurrentSalonId, getCurrentSalonName } from "../utils/currentSalon";
import {
  getCancellationSettings,
  previewCancellationToken,
  updateCancellationSettings,
} from "../api/onlineCancellations";
import { listSalonUsers } from "../api/salonUsers";
import "./OnlineBookingSectionCompact.css";

const DEFAULT_REASONS = [
  "Schedule change",
  "Too busy",
  "Not feeling well",
  "Price",
  "Found another salon",
  "Other",
];

const TOOLTIP_TEXT = {
  cancel_enabled: "Allow clients to cancel their appointments online.",
  cancel_cutoff:
    "Clients can cancel online until this amount of time before the appointment.",
  policy_text:
    "This message is shown on the client page before they take action.",
  late_text:
    "Shown when online changes are closed. Include the salon phone if helpful.",
  reschedule_enabled: "Allow clients to reschedule their appointments online.",
  reschedule_cutoff:
    "Clients can reschedule online until this amount of time before the appointment.",
  require_reason: "Ask clients for a reason when they cancel.",
  reasons:
    "These are the options clients can choose when they cancel online.",
  notify_cancel:
    "Send an internal notification when a client cancels online.",
  notify_reschedule:
    "Send an internal notification when a client reschedules online.",
  notify_emails:
    "Separate multiple emails with commas. These messages are internal only.",
  followup_assignee:
    "Choose who should own follow-up tasks when they are created.",
  followup_cancel:
    "Create a follow-up task when a client cancels.",
  followup_late:
    "Create a follow-up task when a client tries to cancel too late.",
  manage_link:
    "Add the manage link to appointment confirmations and reminders.",
  override:
    "Allow managers to override the cutoff rules from the schedule.",
};

const placeholderToken = "{{cancel_reschedule_link}}";

const HELP_SECTIONS = [
  {
    id: "online-cancel-overview",
    title: "What this page does",
    description: "Control online cancellation/rescheduling rules and client messaging.",
    anchorId: "ocs-overview",
    bullets: [
      "Set cancellation and rescheduling cutoff windows in salon time.",
      "Configure reasons, notifications, and follow-up behavior.",
      "Add secure manage links to appointment emails.",
    ],
  },
  {
    id: "cancel-reschedule-rules",
    title: "Cancellation and rescheduling rules",
    description: "Define client-facing cutoff values, policy text, and late-action handling.",
    anchorId: "ocs-rules",
  },
  {
    id: "followups-notifications",
    title: "Reasons, follow-ups, and notifications",
    description: "Collect cancellation reasons and control internal notifications/tasks.",
    anchorId: "ocs-followups",
  },
  {
    id: "email-links-preview",
    title: "Email links and preview",
    description: "Manage appointment-email placeholders and preview the client flow.",
    anchorId: "ocs-links-preview",
  },
];

const normalizeSettings = (data) => {
  const reasonOptions = Array.isArray(data?.reason_options)
    ? data.reason_options
    : DEFAULT_REASONS;
  return {
    online_cancel_enabled: Boolean(data?.online_cancel_enabled ?? true),
    online_reschedule_enabled: Boolean(data?.online_reschedule_enabled ?? true),
    cancel_cutoff_value: String(data?.cancel_cutoff_value ?? 24),
    cancel_cutoff_unit: data?.cancel_cutoff_unit || "hours",
    reschedule_cutoff_value: String(data?.reschedule_cutoff_value ?? 24),
    reschedule_cutoff_unit: data?.reschedule_cutoff_unit || "hours",
    policy_text: data?.policy_text || "",
    late_action_text: data?.late_action_text || "",
    require_reason_on_cancel: Boolean(data?.require_reason_on_cancel ?? true),
    reason_options: reasonOptions,
    include_manage_link_in_confirmation_email: Boolean(
      data?.include_manage_link_in_confirmation_email ?? true
    ),
    include_manage_link_in_reminder_email: Boolean(
      data?.include_manage_link_in_reminder_email ?? true
    ),
    notify_on_cancel_enabled: Boolean(data?.notify_on_cancel_enabled ?? true),
    notify_on_reschedule_enabled: Boolean(data?.notify_on_reschedule_enabled ?? true),
    notify_emails_csv: data?.notify_emails_csv || "",
    default_followup_assignee_user_id:
      data?.default_followup_assignee_user_id || "",
    create_followup_task_on_late_attempt: Boolean(
      data?.create_followup_task_on_late_attempt ?? false
    ),
    create_followup_task_on_cancel: Boolean(
      data?.create_followup_task_on_cancel ?? false
    ),
    allow_manager_override: Boolean(data?.allow_manager_override ?? true),
    email_configured: Boolean(data?.email_configured ?? true),
  };
};

export default function OnlineCancellationsSettings() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(normalizeSettings(null));
  const [initialForm, setInitialForm] = useState(null);
  const [reasonDraft, setReasonDraft] = useState("");
  const [users, setUsers] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const hasChanges = useMemo(() => {
    if (!initialForm) return false;
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getCancellationSettings(token);
      const normalized = normalizeSettings(data);
      setForm(normalized);
      setInitialForm(normalized);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Unable to load online cancellation settings."
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await listSalonUsers(token);
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setUsers([]);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
    loadUsers();
  }, [loadSettings, loadUsers]);

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const handleSave = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    const payload = {
      online_cancel_enabled: Boolean(form.online_cancel_enabled),
      online_reschedule_enabled: Boolean(form.online_reschedule_enabled),
      cancel_cutoff_value: Number(form.cancel_cutoff_value || 0),
      cancel_cutoff_unit: form.cancel_cutoff_unit,
      reschedule_cutoff_value: Number(form.reschedule_cutoff_value || 0),
      reschedule_cutoff_unit: form.reschedule_cutoff_unit,
      policy_text: form.policy_text,
      late_action_text: form.late_action_text,
      require_reason_on_cancel: Boolean(form.require_reason_on_cancel),
      reason_options: form.reason_options,
      include_manage_link_in_confirmation_email: Boolean(
        form.include_manage_link_in_confirmation_email
      ),
      include_manage_link_in_reminder_email: Boolean(
        form.include_manage_link_in_reminder_email
      ),
      notify_on_cancel_enabled: Boolean(form.notify_on_cancel_enabled),
      notify_on_reschedule_enabled: Boolean(form.notify_on_reschedule_enabled),
      notify_emails_csv: form.notify_emails_csv,
      default_followup_assignee_user_id:
        form.default_followup_assignee_user_id || null,
      create_followup_task_on_late_attempt: Boolean(
        form.create_followup_task_on_late_attempt
      ),
      create_followup_task_on_cancel: Boolean(
        form.create_followup_task_on_cancel
      ),
      allow_manager_override: Boolean(form.allow_manager_override),
    };
    try {
      const data = await updateCancellationSettings(payload, token);
      const normalized = normalizeSettings(data);
      setForm(normalized);
      setInitialForm(normalized);
      setNotice("Online cancellation settings saved.");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Unable to save online cancellation settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddReason = () => {
    const trimmed = reasonDraft.trim();
    if (!trimmed) return;
    setForm((prev) => {
      if (
        prev.reason_options.some(
          (item) => item.toLowerCase() === trimmed.toLowerCase()
        )
      ) {
        return prev;
      }
      return { ...prev, reason_options: [...prev.reason_options, trimmed] };
    });
    setReasonDraft("");
  };

  const handleRemoveReason = (value) => {
    setForm((prev) => ({
      ...prev,
      reason_options: prev.reason_options.filter((item) => item !== value),
    }));
  };

  const handleCopyPlaceholder = async () => {
    try {
      await navigator.clipboard.writeText(placeholderToken);
      setNotice("Link placeholder copied.");
    } catch (err) {
      setNotice("Copy this placeholder from the card instead.");
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setError("");
    setNotice("");
    try {
      const data = await previewCancellationToken({}, token);
      const url = data?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        if (data?.preview_only) {
          setNotice("Preview opened with sample appointment details.");
        }
      } else {
        setError("Preview link unavailable. Please save settings and try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to open preview.");
    } finally {
      setPreviewing(false);
    }
  };

  const jumpToSectionWithHighlight = useCallback((anchorId) => {
    if (!anchorId) return;
    const element = document.getElementById(anchorId);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedSectionId(anchorId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedSectionId("");
    }, 1800);
  }, []);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="onlineBookingShell">
      <div className="onlineBookingWide online-booking-medium online-booking-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <div className="flex flex-wrap items-center justify-between gap-3 pr-12">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-white">
                  Online Cancellations
                </h1>
                <p className="text-sm md:text-base text-rose-100">
                  Control when clients can cancel or reschedule online and what they see.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="rounded-full bg-white/95 px-4 py-2 text-xs md:text-sm font-semibold text-rose-600 hover:bg-white disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            {salonName && (
              <div className="text-xs text-slate-500">
                {salonName}
                {salonId ? ` - Salon ID ${salonId}` : ""}
              </div>
            )}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {notice}
              </div>
            )}

            <div id="ocs-overview">
              <ReportHelpCard
                title="Help Guide: Online Cancellations"
                subtitle="Configure online cancel/reschedule rules, notifications, and manage links."
                sections={HELP_SECTIONS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {!form.email_configured && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-700">
                Email sending is not configured. Links can still be generated, but
                automated emails will not send.
              </div>
            )}

            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-600">
                Loading online cancellation settings...
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div
                    id="ocs-rules"
                    className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 transition ${
                      highlightedSectionId === "ocs-rules" ? "ring-2 ring-rose-300" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Online cancellation rules
                      </h2>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.online_cancel_enabled}
                        onChange={(e) =>
                          handleFieldChange("online_cancel_enabled", e.target.checked)
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="inline-flex items-center gap-2">
                        Enable online cancellation
                        <InfoTooltip
                          text={TOOLTIP_TEXT.cancel_enabled}
                          label="Enable"
                        />
                      </span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                          Cutoff value
                          <InfoTooltip text={TOOLTIP_TEXT.cancel_cutoff} label="Cutoff" />
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={form.cancel_cutoff_value}
                          onChange={(e) =>
                            handleFieldChange("cancel_cutoff_value", e.target.value)
                          }
                          className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Unit
                        </label>
                        <select
                          value={form.cancel_cutoff_unit}
                          onChange={(e) =>
                            handleFieldChange("cancel_cutoff_unit", e.target.value)
                          }
                          className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3"
                        >
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                        Policy text
                        <InfoTooltip text={TOOLTIP_TEXT.policy_text} label="Policy" />
                      </label>
                      <textarea
                        rows={3}
                        value={form.policy_text}
                        onChange={(e) =>
                          handleFieldChange("policy_text", e.target.value)
                        }
                        className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                        Late action text
                        <InfoTooltip text={TOOLTIP_TEXT.late_text} label="Late" />
                      </label>
                      <textarea
                        rows={3}
                        value={form.late_action_text}
                        onChange={(e) =>
                          handleFieldChange("late_action_text", e.target.value)
                        }
                        className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3"
                      />
                    </div>
                  </div>

                  <div
                    id="ocs-followups"
                    className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 transition ${
                      highlightedSectionId === "ocs-followups"
                        ? "ring-2 ring-rose-300"
                        : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Online rescheduling rules
                      </h2>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.online_reschedule_enabled}
                        onChange={(e) =>
                          handleFieldChange("online_reschedule_enabled", e.target.checked)
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="inline-flex items-center gap-2">
                        Enable online rescheduling
                        <InfoTooltip
                          text={TOOLTIP_TEXT.reschedule_enabled}
                          label="Enable"
                        />
                      </span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                          Cutoff value
                          <InfoTooltip
                            text={TOOLTIP_TEXT.reschedule_cutoff}
                            label="Cutoff"
                          />
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={form.reschedule_cutoff_value}
                          onChange={(e) =>
                            handleFieldChange("reschedule_cutoff_value", e.target.value)
                          }
                          className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Unit
                        </label>
                        <select
                          value={form.reschedule_cutoff_unit}
                          onChange={(e) =>
                            handleFieldChange("reschedule_cutoff_unit", e.target.value)
                          }
                          className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3"
                        >
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div
                    id="ocs-links-preview"
                    className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 transition ${
                      highlightedSectionId === "ocs-links-preview"
                        ? "ring-2 ring-rose-300"
                        : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Reasons and follow-ups
                      </h2>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.require_reason_on_cancel}
                        onChange={(e) =>
                          handleFieldChange("require_reason_on_cancel", e.target.checked)
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="inline-flex items-center gap-2">
                        Require a cancellation reason
                        <InfoTooltip
                          text={TOOLTIP_TEXT.require_reason}
                          label="Reason"
                        />
                      </span>
                    </label>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                        Reason options
                        <InfoTooltip text={TOOLTIP_TEXT.reasons} label="Reasons" />
                      </div>
                      <div className="mt-2 space-y-2">
                        {form.reason_options.map((reason) => (
                          <div
                            key={reason}
                            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                          >
                            <span className="text-sm text-slate-700">{reason}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveReason(reason)}
                              className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="text"
                            value={reasonDraft}
                            onChange={(e) => setReasonDraft(e.target.value)}
                            placeholder="Add a reason"
                            className="flex-1 min-w-[180px] rounded-2xl border border-slate-300 px-4 py-2 text-sm"
                          />
                          <button
                            type="button"
                            onClick={handleAddReason}
                            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Add reason
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                          Default follow-up owner
                          <InfoTooltip
                            text={TOOLTIP_TEXT.followup_assignee}
                            label="Owner"
                          />
                        </label>
                        <select
                          value={form.default_followup_assignee_user_id}
                          onChange={(e) =>
                            handleFieldChange(
                              "default_followup_assignee_user_id",
                              e.target.value
                            )
                          }
                          className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3"
                        >
                          <option value="">No default</option>
                          {users.map((userItem) => (
                            <option key={userItem.id} value={userItem.id}>
                              {userItem.full_name || userItem.name || userItem.email}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={form.create_followup_task_on_cancel}
                            onChange={(e) =>
                              handleFieldChange(
                                "create_followup_task_on_cancel",
                                e.target.checked
                              )
                            }
                            className="h-4 w-4 rounded border-slate-300 text-slate-900"
                          />
                          <span className="inline-flex items-center gap-2">
                            Create follow-up task on cancellation
                            <InfoTooltip
                              text={TOOLTIP_TEXT.followup_cancel}
                              label="Task"
                            />
                          </span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={form.create_followup_task_on_late_attempt}
                            onChange={(e) =>
                              handleFieldChange(
                                "create_followup_task_on_late_attempt",
                                e.target.checked
                              )
                            }
                            className="h-4 w-4 rounded border-slate-300 text-slate-900"
                          />
                          <span className="inline-flex items-center gap-2">
                            Create follow-up task on late attempt
                            <InfoTooltip
                              text={TOOLTIP_TEXT.followup_late}
                              label="Task"
                            />
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Notifications
                      </h2>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.notify_on_cancel_enabled}
                        onChange={(e) =>
                          handleFieldChange("notify_on_cancel_enabled", e.target.checked)
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="inline-flex items-center gap-2">
                        Notify on cancellation
                        <InfoTooltip
                          text={TOOLTIP_TEXT.notify_cancel}
                          label="Notify"
                        />
                      </span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.notify_on_reschedule_enabled}
                        onChange={(e) =>
                          handleFieldChange(
                            "notify_on_reschedule_enabled",
                            e.target.checked
                          )
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="inline-flex items-center gap-2">
                        Notify on reschedule
                        <InfoTooltip
                          text={TOOLTIP_TEXT.notify_reschedule}
                          label="Notify"
                        />
                      </span>
                    </label>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                        Notification recipients
                        <InfoTooltip
                          text={TOOLTIP_TEXT.notify_emails}
                          label="Recipients"
                        />
                      </label>
                      <textarea
                        rows={3}
                        value={form.notify_emails_csv}
                        onChange={(e) =>
                          handleFieldChange("notify_emails_csv", e.target.value)
                        }
                        placeholder="team@example.com, owner@example.com"
                        className="mt-2 w-full rounded-2xl border border-slate-300 px-4 py-3"
                      />
                      <div className="text-xs text-slate-500 mt-2">
                        Notifications go to internal recipients only.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Links in emails
                      </h2>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.include_manage_link_in_confirmation_email}
                        onChange={(e) =>
                          handleFieldChange(
                            "include_manage_link_in_confirmation_email",
                            e.target.checked
                          )
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="inline-flex items-center gap-2">
                        Include manage link in confirmation emails
                        <InfoTooltip
                          text={TOOLTIP_TEXT.manage_link}
                          label="Link"
                        />
                      </span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.include_manage_link_in_reminder_email}
                        onChange={(e) =>
                          handleFieldChange(
                            "include_manage_link_in_reminder_email",
                            e.target.checked
                          )
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="inline-flex items-center gap-2">
                        Include manage link in reminder emails
                      </span>
                    </label>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Placeholder
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <span className="font-mono text-xs text-slate-700">
                          {placeholderToken}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopyPlaceholder}
                          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Copy placeholder
                        </button>
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        Use this placeholder in your appointment email templates.
                      </div>
                    </div>
                    <Link
                      to="/manager/online-booking/appointment-emails"
                      className="inline-flex items-center rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Open Appointment Emails
                    </Link>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                        Preview
                      </h2>
                    </div>
                    <div className="text-sm text-slate-600">
                      Preview the client page using a short-lived link.
                    </div>
                    <button
                      type="button"
                      onClick={handlePreview}
                      disabled={previewing}
                      className="inline-flex items-center rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                    >
                      {previewing ? "Opening..." : "Preview client cancellation page"}
                    </button>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.allow_manager_override}
                        onChange={(e) =>
                          handleFieldChange("allow_manager_override", e.target.checked)
                        }
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span className="inline-flex items-center gap-2">
                        Allow manager override
                        <InfoTooltip text={TOOLTIP_TEXT.override} label="Override" />
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
